/**
 * The admin area's decisions, kept out of the view (Phase 26). The database
 * has the final word on all of these (0033, 0036); these give instant
 * feedback and keep the page from offering what would only be refused.
 */

import type { AdminFirm, SupportLogEntry } from '@/lib/data/admin';

/** A suggested slug from a firm name: "Tonie Searle Designs" → "toniesearledesigns". */
export function slugFromName(name: string): string {
  return name.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]/g, '').slice(0, 40);
}

export function createFirmProblem(name: string, slug: string, ownerEmail: string): string | null {
  if (name.trim().length < 2) return 'Give the firm a name.';
  const s = slug.trim().toLowerCase();
  if (s && !/^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/.test(s)) {
    return 'Use 3–40 lowercase letters, numbers and hyphens, starting and ending with a letter or number.';
  }
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(ownerEmail.trim())) return 'Enter the owner’s email address.';
  return null;
}

export function supportReasonProblem(reason: string): string | null {
  return reason.trim().length < 8 ? 'Say why you’re opening this firm (at least 8 characters).' : null;
}

/** "Anderson Interiors — 2 of 6 seats · 1 invited". */
export function seatsText(f: Pick<AdminFirm, 'people' | 'pending' | 'seat_limit'>): string {
  const base = `${f.people} of ${f.seat_limit} seat${f.seat_limit === 1 ? '' : 's'}`;
  return f.pending > 0 ? `${base} · ${f.pending} invited` : base;
}

/** What the firm row should offer. */
export function firmRowActions(f: Pick<AdminFirm, 'status' | 'owners'>): {
  needsOwner: boolean; canSuspend: boolean; canReactivate: boolean; canSupport: boolean;
} {
  return {
    needsOwner: !f.owners,
    canSuspend: f.status === 'active',
    canReactivate: f.status === 'suspended',
    // An empty firm has nothing to look at.
    canSupport: !!f.owners,
  };
}

/** Minutes until a session ends, never negative. */
export function minutesLeft(expiresAt: string, now: Date = new Date()): number {
  return Math.max(0, Math.ceil((new Date(expiresAt).getTime() - now.getTime()) / 60_000));
}

export function sessionEndsText(expiresAt: string, now: Date = new Date()): string {
  const m = minutesLeft(expiresAt, now);
  if (m === 0) return 'ended';
  if (m < 60) return `ends in ${m} min`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return `ends in ${h} h${rest ? ` ${rest} min` : ''}`;
}

/** A support log entry's state, for the log table. */
export function sessionState(s: Pick<SupportLogEntry, 'ended_at' | 'expires_at'>, now: Date = new Date()): string {
  if (s.ended_at) return 'Ended';
  return new Date(s.expires_at) > now ? 'Open' : 'Expired';
}
